import React, { useEffect, useState } from "react";
import { PauseCircle } from "lucide-react";
import api from "../lib/api";
import { notifyHeldChanged } from "./AdminHeldBadge";

const fmt = (t) => (t ? new Date(t).toLocaleString() : "—");

function Alert({ a }) {
  if (!a) return <span className="text-[#9E978E]">No alert</span>;
  const tone = a.status === "sent" ? "text-[#9FD8B0]" : a.status === "failed" ? "text-[#E58CA0]" : "text-[#E3C77A]";
  return <span className={tone}>{a.status}{a.last_error ? ` · ${a.last_error}` : ""} · tries {a.attempts}</span>;
}

function HeldRow({ p, run, onDone }) {
  const [note, setNote] = useState("");
  const act = (decision) => run(async () => {
    try {
      await api.post(`/admin/payments/held/${encodeURIComponent(p.session_id)}/resolve`, { decision, note });
      onDone();
    } finally {
      notifyHeldChanged();
    }
  }, decision === "release" ? "Verified with Stripe and credits granted" : "Closed without credits");
  return (
    <div className="border-b border-[#D4AF37]/10 py-3 text-sm" data-testid={`held-row-${p.session_id}`}>
      <div className="flex flex-wrap gap-x-6 gap-y-1 text-[#D8D2C9]">
        <span className="font-mono text-xs">…{p.session_id.slice(-14)}</span>
        <span>{p.credits} credits · {p.amount} {String(p.currency || "usd").toUpperCase()}</span>
        <span className="text-[#E58CA0]" data-testid={`held-reason-${p.session_id}`}>{p.review_reason}</span>
        <span className="text-[#9E978E]">held {fmt(p.review_hold_at)}</span>
        <span data-testid={`held-alert-${p.session_id}`}>Alert: <Alert a={p.alert} /></span>
      </div>
      {(p.review_attempts || []).slice(-2).map((x, i) => (
        <p key={i} className="text-xs text-[#9E978E] mt-1">Attempt {fmt(x.at)} · {x.decision} · {x.result}: {x.reason}</p>))}
      <div className="flex flex-wrap gap-2 mt-2 items-center">
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Audit note (required, 10+ characters)"
          className="flex-1 min-w-[260px] bg-transparent border border-[#D4AF37]/25 rounded-full px-3 py-1.5 text-xs text-[#FBF8F3]"
          data-testid={`held-note-${p.session_id}`} />
        <button disabled={note.trim().length < 10} onClick={() => act("release")} data-testid={`held-release-${p.session_id}`}
          className="text-xs rounded-full bg-[#D4AF37] text-[#0C0A0A] px-3 py-1.5 disabled:opacity-40">Verify with Stripe &amp; release</button>
        <button disabled={note.trim().length < 10} onClick={() => act("reject")} data-testid={`held-reject-${p.session_id}`}
          className="text-xs rounded-full border border-[#8B263E] text-[#E58CA0] px-3 py-1.5 disabled:opacity-40">Close without credits</button>
      </div>
    </div>
  );
}

export const AdminHeldPayments = ({ run }) => {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const load = () => api.get("/admin/payments/held").then((r) => { setD(r.data); setErr(""); })
    .catch(() => setErr("Could not load held payments"));
  useEffect(() => { load(); }, []);
  if (err) return <p className="text-sm text-[#E58CA0] mb-6" data-testid="held-payments-error">{err}</p>;
  if (!d) return null;
  return (
    <div className="rounded-2xl border border-[#D4AF37]/20 bg-[#0C0A0A]/60 p-5 mb-6" data-testid="held-payments-card">
      <div className="flex items-center justify-between">
        <h3 className="font-serif text-lg text-[#FBF8F3] flex items-center gap-2"><PauseCircle size={18} /> Held payments ({d.held.length})</h3>
        <button onClick={() => run(async () => { await api.post("/admin/payments/held/alerts/retry"); load(); }, "Alert retry done")}
          className="text-xs rounded-full border border-[#D4AF37]/40 px-3 py-1.5 text-[#D4AF37]" data-testid="held-alerts-retry-btn">Retry failed alerts</button>
      </div>
      <p className="text-xs text-[#9E978E] mt-1 mb-2">Release re-checks the payment with Stripe and only grants credits if every check passes. Closing grants nothing; refunds are a separate action.</p>
      {d.held.length === 0 && <p className="text-sm text-[#9E978E]" data-testid="held-payments-empty">No payments are waiting for review.</p>}
      {d.held.map((p) => <HeldRow key={p.session_id} p={p} run={run} onDone={load} />)}
      {d.resolved.length > 0 && <p className="text-xs text-[#9E978E] mt-3" data-testid="held-resolved-count">{d.resolved.length} recently resolved</p>}
    </div>
  );
};

export default AdminHeldPayments;
